import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { apiClient } from '../lib/apiClient';
import EnforcementModal from './EnforcementModal';
import { 
  X, 
  CheckSquare, 
  Square, 
  Users, 
  Lock, 
  Hash,
  AlertCircle,
  Download,
  CheckCircle,
  RefreshCw,
  Info,
  MessageSquare,
  Plus
} from 'lucide-react';

interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
  member_count: number;
  is_member: boolean;
  purpose?: string;
  topic?: string;
}

interface ChannelManagementData {
  joined_channels: SlackChannel[];
  unjoined_public_channels: SlackChannel[];
  private_channels: SlackChannel[];
  summary: {
    total_channels: number;
    joined_count: number;
    unjoined_public_count: number;
    private_count: number;
  };
}

interface SlackImportModalProps {
  onClose: () => void;
}

const SlackImportModal: React.FC<SlackImportModalProps> = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<string>('ready');
  const [showEnforcementModal, setShowEnforcementModal] = useState(false);
  const [enforcementError, setEnforcementError] = useState<any>(null);

  // Fetch channel management data
  const { data: channelData, isLoading, error, refetch } = useQuery<ChannelManagementData>({
    queryKey: ['slack-channel-management'],
    queryFn: async () => {
      return apiClient.customRequest('/api/integrations/slack/channels');
    },
    // Always fetch fresh data when modal opens
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true
  });

  // Join public channel mutation
  const joinChannelMutation = useMutation<any, Error, string>({
    mutationFn: async (channelId: string) => {
      return apiClient.joinSlackChannel(channelId);
    },
    onSuccess: (_, channelId) => {
      // Refresh channel data to move the channel from "Public Channels" to "Ready to Import"
      refetch();
      alert(`Successfully joined channel! It will now appear in "Ready to Import".`);
    },
    onError: (error) => {
      alert(`Failed to join channel: ${error.message}`);
    }
  });

  // Import selected channels mutation
  const importMutation = useMutation<any, Error, string[]>({
    mutationFn: async (channelIds: string[]) => {
      return apiClient.customRequest('/api/integrations/slack/import-channels', {
        method: 'POST',
        body: { channel_ids: channelIds }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      onClose();
      alert('Data import started successfully! This may take a few minutes to complete.');
    },
    onError: (error: any) => {
      console.log('Import error:', error);
      console.log('Error response:', error.response);
      console.log('Error data:', error.response?.data);
      
      // Check if this is a plan enforcement error
      if (error.response && error.response.status === 403) {
        const errorData = error.response.data;
        console.log('Error data structure:', JSON.stringify(errorData, null, 2));
        
        // Handle multiple possible error structures
        let enforcementError = null;
        
        if (errorData.error) {
          enforcementError = errorData.error;
        } else if (errorData.detail) {
          enforcementError = errorData.detail;
        } else if (typeof errorData === 'string') {
          enforcementError = { message: errorData };
        } else if (errorData.message) {
          enforcementError = errorData;
        } else {
          enforcementError = errorData;
        }
        
        console.log('Processed enforcement error:', enforcementError);
        
        // Check if this is a plan limit error
        if (enforcementError && (
          enforcementError.error === 'plan_limit_exceeded' || 
          enforcementError.message?.includes('Limit of') ||
          enforcementError.message?.includes('plan limit') ||
          enforcementError.message?.includes('upgrade your plan')
        )) {
          console.log('Setting enforcement error:', enforcementError);
          setEnforcementError(enforcementError);
          setShowEnforcementModal(true);
        } else {
          alert(`Failed to import data: ${enforcementError?.message || error.message}`);
        }
      } else {
        alert(`Failed to import data: ${error.message}`);
      }
    }
  });

  const toggleChannelSelection = (channelId: string) => {
    const newSelected = new Set(selectedChannels);
    if (newSelected.has(channelId)) {
      newSelected.delete(channelId);
    } else {
      newSelected.add(channelId);
    }
    setSelectedChannels(newSelected);
  };

  const selectAllChannels = () => {
    if (!channelData) return;
    
    let allChannelIds: string[] = [];
    if (activeTab === 'ready') {
      allChannelIds = channelData.joined_channels?.map(c => c.id) || [];
    } else if (activeTab === 'private') {
      allChannelIds = channelData.private_channels?.map(c => c.id) || [];
    }
    
    setSelectedChannels(new Set(allChannelIds));
  };

  const selectNone = () => {
    setSelectedChannels(new Set());
  };

  const handleJoinChannel = (channelId: string) => {
    joinChannelMutation.mutate(channelId);
  };

  const handleImport = () => {
    if (selectedChannels.size === 0) {
      alert('Please select at least one channel to import');
      return;
    }
    importMutation.mutate(Array.from(selectedChannels));
  };

  const getCurrentChannels = () => {
    if (!channelData) return [];
    
    if (activeTab === 'ready') {
      return channelData.joined_channels || [];
    } else if (activeTab === 'public') {
      return channelData.unjoined_public_channels || [];
    } else if (activeTab === 'private') {
      return channelData.private_channels || [];
    }
    
    return [];
  };

  const canImport = () => {
    return activeTab === 'ready' || activeTab === 'private';
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-4xl">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="lg" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading channels...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Error Loading Channels</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {error.message || 'Failed to load channel data'}
              </p>
              <Button onClick={onClose}>Close</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-5xl max-h-[90vh] flex flex-col dark:bg-gray-800">
        <CardHeader className="border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl dark:text-gray-100">Import Slack Data</CardTitle>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Manage your Slack channel access and import data from connected channels
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetch()}
                disabled={isLoading}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                title="Refresh channel data"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {channelData && (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="ready" className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Ready to Import
                  <Badge variant="secondary" className="ml-2 bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200">
                    {channelData.summary?.joined_count || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="public" className="flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  Public Channels
                  <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                    {channelData.summary?.unjoined_public_count || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="private" className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Private Channels
                  <Badge variant="secondary" className="ml-2 bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-200">
                    {channelData.summary?.private_count || 0}
                  </Badge>
                </TabsTrigger>
              </TabsList>

              {/* Ready to Import Tab */}
              <TabsContent value="ready" className="mt-6">
                <div className="h-[400px] overflow-y-auto">
                  <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Select the channels you want to import data from
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={selectAllChannels}
                        disabled={importMutation.isPending}
                      >
                        Select All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={selectNone}
                        disabled={importMutation.isPending}
                      >
                        Select None
                      </Button>
                    </div>
                  </div>

                  {(channelData.joined_channels?.length || 0) === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Hash className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-medium mb-2">No Channels Ready</h3>
                      <p className="text-sm">
                        You haven't joined any public channels yet. Check the "Public Channels" tab to join channels.
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {channelData.joined_channels?.map((channel) => (
                        <div
                          key={channel.id}
                          className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => toggleChannelSelection(channel.id)}
                              className="flex-shrink-0"
                            >
                              {selectedChannels.has(channel.id) ? (
                                <CheckSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <Hash className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {channel.name}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {channel.member_count} members
                                </Badge>
                              </div>
                              {channel.purpose && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                                  {channel.purpose}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  </div>
                </div>
              </TabsContent>

              {/* Public Channels Tab */}
              <TabsContent value="public" className="mt-6">
                <div className="h-[400px] overflow-y-auto">
                  <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Join public channels to make them available for import
                    </div>
                  </div>

                  {(channelData.unjoined_public_channels?.length || 0) === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Hash className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-medium mb-2">No Public Channels Available</h3>
                      <p className="text-sm">
                        All public channels in your workspace are already joined or there are no public channels.
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {channelData.unjoined_public_channels?.map((channel) => (
                        <div
                          key={channel.id}
                          className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <Hash className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {channel.name}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {channel.member_count} members
                                </Badge>
                              </div>
                              {channel.purpose && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                                  {channel.purpose}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            onClick={() => handleJoinChannel(channel.id)}
                            disabled={joinChannelMutation.isPending}
                            size="sm"
                            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                          >
                            {joinChannelMutation.isPending ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Joining...
                              </>
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-2" />
                                Join & Prepare for Import
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  </div>
                </div>
              </TabsContent>

              {/* Private Channels Tab */}
              <TabsContent value="private" className="mt-6">
                <div className="h-[400px] overflow-y-auto">
                  <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Select private channels you have access to for import
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={selectAllChannels}
                        disabled={importMutation.isPending}
                      >
                        Select All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={selectNone}
                        disabled={importMutation.isPending}
                      >
                        Select None
                      </Button>
                    </div>
                  </div>

                  {(channelData.private_channels?.length || 0) === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <Lock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-medium mb-2">No Private Channels</h3>
                      <p className="text-sm">
                        You don't have access to any private channels in this workspace.
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {channelData.private_channels?.map((channel) => (
                        <div
                          key={channel.id}
                          className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => toggleChannelSelection(channel.id)}
                              className="flex-shrink-0"
                            >
                              {selectedChannels.has(channel.id) ? (
                                <CheckSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                              )}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <Lock className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {channel.name}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {channel.member_count} members
                                </Badge>
                              </div>
                              {channel.purpose && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
                                  {channel.purpose}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </CardContent>

        {/* Fixed Footer with Import Button */}
        {channelData && (activeTab === 'ready' || activeTab === 'private') && (
          <CardFooter className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
            <div className="flex justify-end w-full">
              <Button
                onClick={handleImport}
                disabled={selectedChannels.size === 0 || importMutation.isPending}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
              >
                {importMutation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Import Selected Channels ({selectedChannels.size})
                  </>
                )}
              </Button>
            </div>
          </CardFooter>
        )}
      </Card>

      {/* Enforcement Modal */}
      {showEnforcementModal && (
        <EnforcementModal
          isOpen={showEnforcementModal}
          error={enforcementError}
          onClose={() => {
            setShowEnforcementModal(false);
            setEnforcementError(null);
          }}
        />
      )}
    </div>
  );
};

export default SlackImportModal;